<template>
  <div>
    <!-- Loading Overlay -->
    <b-overlay :show="loadingCustomer || isLoading" spinner-variant="primary" rounded="sm">
      <!-- Form -->
      <div class="mt-2">
        <customer-form
          :form-data.sync="formData"
          :loading="isSubmitting"
          @submit="onSubmit"
          v-if="!loadingCustomer"
        />
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { BOverlay } from 'bootstrap-vue';
import { ref, computed, onMounted } from '@vue/composition-api';
import store from '@/store';
import { api } from '@/utils/api';
import CustomerForm from './CustomerForm.vue';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CustomerEdit',
  components: {
    BOverlay,
    CustomerForm,
    ToastificationContent,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props, { root }) {
    const toast = useToast();
    const businessID = computed(() => store.state.user.businessID);
    const userID = computed(() => store.state.user.userID);

    // Loading states
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const formData = ref(null);
    const loadingCustomer = ref(true);

    const fetchCustomerData = async () => {
      if (!props.id) return;

      loadingCustomer.value = true;
      try {
        // Direct API call to LoadSelectedData
        const response = await api.get(
          `/CustomersFBR/LoadSelectedData/${businessID.value}?id=${props.id}&userID=${userID.value}`
        );

        console.log('=== CustomerEdit fetchCustomerData response:', response);

        // Handle different possible response structures
        let fetchedCustomer = null;

        if (response.data && response.data.data && response.data.data.entity) {
          fetchedCustomer = response.data.data.entity;
        } else {
          console.error('Unexpected response structure:', response);
          throw new Error('Invalid response structure from API');
        }

        console.log('FETCHED CUSTOMER:', JSON.stringify(fetchedCustomer, null, 2));

        // Map API fields to form fields
        formData.value = {
          id: fetchedCustomer.id || 0,
          code: fetchedCustomer.code || '',
          name: fetchedCustomer.name || '',
          shortName: fetchedCustomer.shortName || '',
          cellNumber: fetchedCustomer.cellNumber || '',
          category: fetchedCustomer.category || '',
          subCategoryID: fetchedCustomer.subCategoryID || null,
          email: fetchedCustomer.email || '',
          ntnNumber: fetchedCustomer.ntnNumber || '',
          gstNumber: fetchedCustomer.gstNumber || '',
          address: fetchedCustomer.address || '',
          province: fetchedCustomer.province || '',
          cnicNumber: fetchedCustomer.cnicNumber || '',
          contactPerson: fetchedCustomer.contactPerson || '',
          whatsappOrUAN: fetchedCustomer.whatsappOrUAN || '',
          website: fetchedCustomer.website || '',
          commissionPercent: fetchedCustomer.commissionPercent || 0,
          isActive: fetchedCustomer.isActive !== undefined ? fetchedCustomer.isActive : true,
        };

        console.log('=== CustomerEdit populated data:', JSON.stringify(formData.value, null, 2));
      } catch (error) {
        console.error('Error fetching customer:', error);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load customer data',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        loadingCustomer.value = false;
      }
    };

    const onSubmit = async () => {
      console.log('=== Customer Edit On Submit', JSON.stringify(formData, null, 2));

      isSubmitting.value = true;

      try {
        // Prepare payload with entity wrapper
        const payload = {
          entity: {
            id: formData.value.id,
            businessID: businessID.value,
            code: formData.value.code,
            name: formData.value.name,
            shortName: formData.value.shortName,
            commissionPercent: formData.value.commissionPercent,
            accountID: 0,
            accountTitle: '',
            category: formData.value.category,
            subCategoryID: formData.value.subCategoryID,
            address: formData.value.address,
            province: formData.value.province,
            gstNumber: formData.value.gstNumber.replace(/-/g, ''),
            ntnNumber: formData.value.ntnNumber.replace(/-/g, ''),
            cnicNumber: formData.value.cnicNumber.replace(/-/g, ''),
            contactPerson: formData.value.contactPerson,
            cellNumber: formData.value.cellNumber.replace(/-/g, ''),
            whatsappOrUAN: formData.value.whatsappOrUAN,
            telephoneNumber: '',
            email: formData.value.email,
            website: formData.value.website,
            remarks: '',
            isActive: formData.value.isActive,
          },
        };

        console.log('=== CustomerEdit Submission data:', payload);

        // Direct API call
        const response = await api.post(
          `/CustomersFBR/Save/${businessID.value}?userID=${userID.value}`,
          payload
        );

        console.log('=== Customer Edit API Response:', response);

        // Show success toast
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Customer updated successfully',
            variant: 'success',
            icon: 'CheckCircleIcon',
          },
        });

        // Navigate back to customer list using root.$router
        root.$router.push('/apps/fbr-invoice/customer');
      } catch (error) {
        console.error('Error updating customer:', error);

        // Show user-friendly error message
        let errorMessage = 'Error updating customer. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: errorMessage,
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    // Fetch customer data when component mounts
    onMounted(() => {
      fetchCustomerData();
    });

    return {
      formData,
      loadingCustomer,
      isLoading,
      isSubmitting,
      onSubmit,
    };
  },
};
</script>

<style lang="scss" scoped>
// Add any custom styles here
</style>
